'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import FilterIcon from '@/fo/icons/base/icons/filter.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { formattedPrice } from '@/fo/ui/kit/price/base';

import { Filter, Filters, SelectedFilters } from '../../types';
import { CheckboxFilter } from '../FilterOptions/CheckboxFilter/CheckboxFilter';
import { LabelListFilter } from '../FilterOptions/LabelListFilter/LabelListFilter';
import { RangeFilter } from '../FilterOptions/RangeFilter/RangeFilter';
import { ToggleListFilter } from '../FilterOptions/ToggleListFilter/ToggleListFilter';

import { styles } from './FiltersPanel.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface FiltersPanelProps {
  onClick?: NativeElement['onClick'];
  filters: Filters['mainFilters'];
  selectedFilters: SelectedFilters;
  onFilterChange: (
    slug: string,
    value: string | [number, number],
    checked: boolean
  ) => void;
  onReset: () => void;
  onShowProducts: () => void;
  productsCount: number;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
  isOpenModal?: boolean;
}
export function FiltersPanel({
  filters = [],
  selectedFilters,
  onFilterChange,
  onReset,
  onShowProducts,
  productsCount = 0,
  onOpenChange,
  isLoading = false,
  isOpenModal = false
}: FiltersPanelProps) {
  const slots = styles();
  const t = useTranslations();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpenModal && isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal]);

  const openDrawer = (open: boolean) => {
    if (open) {
      setIsOpen(true);
    }
    onOpenChange?.(open);
  };

  const selectedFiltersCount = Object.values(selectedFilters)?.length;

  const trigger = (
    <UiKitButtonBase
      className={slots.filterButton()}
      childrenClassName={slots.filterButtonChildren()}
      color="primary"
    >
      <FilterIcon className={slots.filterIcon()} />
      {t('filters')}
      {selectedFiltersCount > 0 && (
        <UiKitLabelBase
          size="s"
          color="gr"
        >
          {selectedFiltersCount}
        </UiKitLabelBase>
      )}
    </UiKitButtonBase>
  );

  const getFilterOptions = (filter: Filter) => {
    if (!filter) return <div></div>;

    switch (filter.type) {
      case 'checkbox':
        return (
          <CheckboxFilter
            selected={selectedFilters[filter.slug]}
            disabled={filter.disabled}
            onChange={(value, checked) =>
              onFilterChange(filter.slug, value, Boolean(checked))
            }
            {...filter}
          />
        );
      case 'toggleList':
        return (
          <ToggleListFilter
            selected={selectedFilters[filter.slug]}
            disabled={filter.disabled}
            onChange={(value, checked) =>
              onFilterChange(filter.slug, value, Boolean(checked))
            }
            {...filter}
          />
        );
      case 'labelList':
        return (
          <LabelListFilter
            selected={selectedFilters[filter?.slug] || false}
            disabled={filter.disabled}
            onChange={(value, checked) =>
              onFilterChange(filter.slug, value, Boolean(checked))
            }
            {...filter}
          />
        );
      case 'range':
        return (
          <RangeFilter
            selected={selectedFilters[filter.slug]}
            onChange={(value, checked) =>
              onFilterChange(filter.slug, value, Boolean(checked))
            }
            {...filter}
            options={{
              ...filter.options,
              ...{
                fromLabel: `${t('from')} ${formattedPrice(filter.options.from)}`,
                toLabel: `${t('up to')} ${formattedPrice(filter.options.to)}`
              }
            }}
          />
        );
    }
  };

  return (
    <UiKitDrawerBase
      title={t('filters')}
      direction="left"
      trigger={trigger}
      contentClassName={slots.drawer()}
      withoutMobile
      onOpenChange={openDrawer}
      open={isOpen}
    >
      <div className={slots.base()}>
        <div className={slots.titleWrapper()}>
          <h2 className={slots.title()}>{t('filters')}</h2>
          {(Object.keys(selectedFilters).length > 0 ||
            filters?.length === 0) && (
            <p
              onClick={onReset}
              className={slots.resetButton()}
            >
              {t('reset')}
            </p>
          )}
        </div>
        <div className={slots.filters()}>
          {Boolean(filters?.length) &&
            filters?.map((filter: Filter) => (
              <>
                {typeof filter !== 'undefined' && (
                  <div
                    key={filter.slug}
                    className={slots.filter()}
                  >
                    {filter?.label && (
                      <h2
                        className={slots.filterTitle({
                          hide: true
                        })}
                      >
                        {filter.label}
                      </h2>
                    )}
                    {getFilterOptions(filter)}
                  </div>
                )}
              </>
            ))}
        </div>
        <div className={slots.footer()}>
          <UiKitButtonBase
            className={slots.showProductsButton()}
            onClick={onShowProducts}
            disabled={productsCount === 0}
            loading={isLoading}
            color="secondary"
          >
            {t('productCatalog.showProductByCount', {
              count: productsCount
            })}
          </UiKitButtonBase>
        </div>
      </div>
    </UiKitDrawerBase>
  );
}
